import { z } from 'zod';
import { CORRELATION_HEADER, DEV_IDENTITY_CSRF_HEADER, DEV_SERVICE_SECRET_HEADER, PLATFORM_API, adminMeSchema, adminOrganizationDetailSchema, adminOrganizationListSchema, applicationsResponseSchema, devFixtureSchema, devTokenResponseSchema, domainResolutionSchema, internalAccessCheckResponseSchema, internalInvitationResponseSchema, internalMemberDirectorySchema, internalOrganizationSettingsSchema, invitationAcceptResponseSchema, invitationPreviewSchema, loginDiscoveryResponseSchema, loginOptionsSchema, meAccessSchema, navigationDecisionSchema, problemSchema, } from '@traceio-org/platform-contracts';
import { createAdminMethods } from './admin.js';
import { createBillingMethods, createInternalBillingMethods, } from './billing.js';
import { createOrganizationMethods } from './organization.js';
import { createSelfServeMethods } from './self-serve.js';
import { createTicketMethods } from './tickets.js';
import { PlatformApiError, synthesizedProblem } from './errors.js';
// RFC 6750 b64token: anything else (spaces, line breaks, a "Bearer " prefix) is a caller bug, refused
// before a request is made instead of producing a malformed or split Authorization header.
const BEARER_TOKEN = /^[A-Za-z0-9\-._~+/]+=*$/;
/**
 * The contract defines the fixture item but not the list envelope, so both a bare array and
 * `{ fixtures: [...] }` are accepted. Each item is still parsed by the contract schema.
 */
const devFixturesResponseSchema = z.union([
    z.array(devFixtureSchema),
    z.object({ fixtures: z.array(devFixtureSchema) }).transform((value) => value.fixtures),
]);
function normalizeBaseUrl(value) {
    let url;
    try {
        url = new URL(value);
    }
    catch {
        throw new TypeError(`platform-client: baseUrl is not a URL: ${value}`);
    }
    if (url.protocol !== 'http:' && url.protocol !== 'https:') {
        throw new TypeError('platform-client: baseUrl must use http or https');
    }
    if (url.username || url.password || url.search || url.hash) {
        throw new TypeError('platform-client: baseUrl must not carry credentials, a query or a fragment');
    }
    return `${url.origin}${url.pathname.replace(/\/+$/, '')}`;
}
function randomCorrelationId() {
    const cryptoApi = globalThis.crypto;
    if (typeof cryptoApi?.randomUUID === 'function')
        return cryptoApi.randomUUID();
    // randomUUID needs a secure context; fall back to a version 4 UUID from getRandomValues.
    const bytes = new Uint8Array(16);
    cryptoApi.getRandomValues(bytes);
    bytes[6] = ((bytes[6] ?? 0) & 0x0f) | 0x40;
    bytes[8] = ((bytes[8] ?? 0) & 0x3f) | 0x80;
    const hex = Array.from(bytes, (b) => b.toString(16).padStart(2, '0')).join('');
    return `${hex.slice(0, 8)}-${hex.slice(8, 12)}-${hex.slice(12, 16)}-${hex.slice(16, 20)}-${hex.slice(20)}`;
}
function isAbortError(error) {
    return typeof error === 'object' && error !== null && error.name === 'AbortError';
}
async function readJson(response) {
    try {
        const text = await response.text();
        if (!text)
            return { ok: false };
        return { ok: true, value: JSON.parse(text) };
    }
    catch {
        return { ok: false };
    }
}
export function createPlatformClient(options) {
    const baseUrl = normalizeBaseUrl(options.baseUrl);
    // Calling an unbound window.fetch throws "Illegal invocation" in browsers, so wrap it.
    const doFetch = options.fetch ?? ((url, init) => globalThis.fetch(url, init));
    const newCorrelationId = options.createCorrelationId ?? randomCorrelationId;
    async function request(spec) {
        const correlationId = newCorrelationId();
        const headers = new Headers({ accept: 'application/json', [CORRELATION_HEADER]: correlationId });
        if (spec.body !== undefined)
            headers.set('content-type', 'application/json');
        for (const [name, value] of Object.entries(spec.headers ?? {}))
            headers.set(name, value);
        if (spec.auth === 'bearer') {
            let token;
            if (spec.accessToken !== undefined) {
                if (!BEARER_TOKEN.test(spec.accessToken)) {
                    throw new TypeError(`platform-client: the access token given for ${spec.route} is not a bearer token`);
                }
                token = spec.accessToken;
            }
            else {
                if (!options.getAccessToken) {
                    throw new Error(`platform-client: getAccessToken is required to call ${spec.route}`);
                }
                // Errors from the token source (for example a session-expired error) propagate unchanged.
                token = await options.getAccessToken();
            }
            headers.set('authorization', `Bearer ${token}`);
        }
        else if (spec.auth === 'dev-identity') {
            // A cross-site form cannot set a custom header, so the API rejects dev identity calls without it.
            headers.set(DEV_IDENTITY_CSRF_HEADER, '1');
        }
        const search = new URLSearchParams();
        for (const [key, value] of Object.entries(spec.query ?? {})) {
            if (value !== undefined && value !== '')
                search.set(key, String(value));
        }
        const queryString = search.toString();
        const url = `${baseUrl}${spec.path}${queryString ? `?${queryString}` : ''}`;
        const init = {
            method: spec.method,
            headers,
            // Only the development identity calls may carry cookies; everything else is token based.
            credentials: spec.auth === 'dev-identity' ? 'include' : 'omit',
            // Access answers change with memberships; never let the HTTP cache serve a stale one.
            cache: 'no-store',
            // Following a redirect could replay the Authorization header elsewhere; the API never redirects.
            redirect: 'error',
        };
        if (spec.body !== undefined)
            init.body = JSON.stringify(spec.body);
        if (spec.signal)
            init.signal = spec.signal;
        let response;
        try {
            response = await doFetch(url, init);
        }
        catch (error) {
            if (isAbortError(error))
                throw error;
            throw new PlatformApiError({
                kind: 'network',
                status: 0,
                problem: synthesizedProblem(0, 'platform-api could not be reached'),
                correlationId,
                route: spec.route,
                cause: error,
            });
        }
        if (!response.ok) {
            const body = await readJson(response);
            const parsed = body.ok ? problemSchema.safeParse(body.value) : null;
            throw new PlatformApiError({
                kind: 'http',
                status: response.status,
                problem: parsed?.success
                    ? parsed.data
                    : synthesizedProblem(response.status, response.statusText || `HTTP ${response.status}`),
                correlationId,
                route: spec.route,
            });
        }
        if (!spec.schema) {
            // Drain the body so the connection can be reused; its content is not part of the contract.
            await response.text().catch(() => undefined);
            return undefined;
        }
        const body = await readJson(response);
        const parsed = body.ok ? spec.schema.safeParse(body.value) : null;
        if (!parsed?.success) {
            throw new PlatformApiError({
                kind: 'invalid_response',
                status: response.status,
                problem: synthesizedProblem(response.status, 'platform-api returned a response outside the contract', 'internal'),
                correlationId,
                route: spec.route,
                cause: parsed && !parsed.success ? parsed.error : undefined,
            });
        }
        return parsed.data;
    }
    return {
        baseUrl,
        ...createAdminMethods(request),
        ...createBillingMethods(request),
        ...createInternalBillingMethods(request),
        ...createOrganizationMethods(request),
        ...createTicketMethods(request),
        ...createSelfServeMethods(request),
        applications: (opts) => request({
            route: PLATFORM_API.applications,
            path: PLATFORM_API.applications,
            method: 'GET',
            auth: 'none',
            schema: applicationsResponseSchema,
            signal: opts?.signal,
        }),
        resolveDomain: (hostname, opts) => request({
            route: PLATFORM_API.resolveDomain,
            path: PLATFORM_API.resolveDomain,
            method: 'GET',
            auth: 'none',
            query: { host: hostname },
            schema: domainResolutionSchema,
            signal: opts?.signal,
        }),
        loginOptions: (productKey, opts) => request({
            route: PLATFORM_API.loginOptions,
            path: PLATFORM_API.loginOptions,
            method: 'GET',
            auth: 'none',
            query: { product: productKey },
            schema: loginOptionsSchema,
            signal: opts?.signal,
        }),
        discover: (body, opts) => request({
            route: PLATFORM_API.loginDiscover,
            path: PLATFORM_API.loginDiscover,
            method: 'POST',
            auth: 'none',
            body,
            schema: loginDiscoveryResponseSchema,
            signal: opts?.signal,
        }),
        meAccess: (opts) => request({
            route: PLATFORM_API.meAccess,
            path: PLATFORM_API.meAccess,
            method: 'GET',
            auth: 'bearer',
            schema: meAccessSchema,
            signal: opts?.signal,
            accessToken: opts?.accessToken,
        }),
        decide: (body, opts) => request({
            route: PLATFORM_API.navigationDecide,
            path: PLATFORM_API.navigationDecide,
            method: 'POST',
            auth: 'bearer',
            body,
            schema: navigationDecisionSchema,
            signal: opts?.signal,
            accessToken: opts?.accessToken,
        }),
        invitationPreview: (token, opts) => request({
            route: '/v1/invitations/:token',
            path: PLATFORM_API.invitationPreview(token),
            method: 'GET',
            auth: 'none',
            schema: invitationPreviewSchema,
            signal: opts?.signal,
        }),
        acceptInvitation: (token, opts) => request({
            route: '/v1/invitations/:token/accept',
            path: PLATFORM_API.invitationAccept(token),
            method: 'POST',
            auth: 'bearer',
            // An empty JSON object keeps servers that validate a body schema satisfied.
            body: {},
            schema: invitationAcceptResponseSchema,
            signal: opts?.signal,
            accessToken: opts?.accessToken,
        }),
        devFixtures: (opts) => request({
            route: PLATFORM_API.devFixtures,
            path: PLATFORM_API.devFixtures,
            method: 'GET',
            auth: 'dev-identity',
            schema: devFixturesResponseSchema,
            signal: opts?.signal,
        }),
        devStartSession: (fixtureKey, opts) => request({
            route: PLATFORM_API.devSession,
            path: PLATFORM_API.devSession,
            method: 'POST',
            auth: 'dev-identity',
            body: { fixtureKey },
            signal: opts?.signal,
        }),
        devToken: (audience, opts) => request({
            route: PLATFORM_API.devToken,
            path: PLATFORM_API.devToken,
            method: 'POST',
            auth: 'dev-identity',
            body: { audience, ...(opts?.contextId !== undefined ? { contextId: opts.contextId } : {}) },
            schema: devTokenResponseSchema,
            signal: opts?.signal,
        }),
        devServiceToken: (principal, secret, opts) => request({
            route: PLATFORM_API.devServiceToken,
            path: PLATFORM_API.devServiceToken,
            method: 'POST',
            // A server-to-server call: no cookie and no browser CSRF header, only the service secret.
            auth: 'none',
            headers: { [DEV_SERVICE_SECRET_HEADER]: secret },
            body: { principal },
            schema: devTokenResponseSchema,
            signal: opts?.signal,
        }),
        internalAccessCheck: (body, opts) => request({
            route: PLATFORM_API.internalAccessCheck,
            path: PLATFORM_API.internalAccessCheck,
            method: 'POST',
            auth: 'bearer',
            body,
            schema: internalAccessCheckResponseSchema,
            signal: opts?.signal,
            accessToken: opts?.accessToken,
        }),
        internalInvitation: (body, opts) => request({
            route: PLATFORM_API.internalInvitations,
            path: PLATFORM_API.internalInvitations,
            method: 'POST',
            auth: 'bearer',
            body,
            schema: internalInvitationResponseSchema,
            signal: opts?.signal,
            accessToken: opts?.accessToken,
        }),
        internalOrganizationSettings: (organizationId, query, opts) => request({
            route: '/v1/internal/organizations/:orgId/settings',
            path: PLATFORM_API.internalOrganizationSettings(organizationId),
            method: 'GET',
            auth: 'bearer',
            query: { productKey: query.productKey },
            schema: internalOrganizationSettingsSchema,
            signal: opts?.signal,
            accessToken: opts?.accessToken,
        }),
        internalOrganizationMembers: (organizationId, query, opts) => request({
            route: '/v1/internal/organizations/:orgId/members',
            path: PLATFORM_API.internalOrganizationMembers(organizationId),
            method: 'GET',
            auth: 'bearer',
            query: {
                productKey: query.productKey,
                email: query.email,
                userId: query.userId,
                status: query.status,
                cursor: query.cursor,
                limit: query.limit,
            },
            schema: internalMemberDirectorySchema,
            signal: opts?.signal,
            accessToken: opts?.accessToken,
        }),
        devEndSession: (opts) => request({
            route: PLATFORM_API.devSession,
            path: PLATFORM_API.devSession,
            method: 'DELETE',
            auth: 'dev-identity',
            signal: opts?.signal,
        }),
        adminMe: (opts) => request({
            route: PLATFORM_API.adminMe,
            path: PLATFORM_API.adminMe,
            method: 'GET',
            auth: 'bearer',
            schema: adminMeSchema,
            signal: opts?.signal,
            accessToken: opts?.accessToken,
        }),
        adminOrganizations: (query, opts) => request({
            route: PLATFORM_API.adminOrganizations,
            path: PLATFORM_API.adminOrganizations,
            method: 'GET',
            auth: 'bearer',
            query: { query: query?.query, cursor: query?.cursor, limit: query?.limit },
            schema: adminOrganizationListSchema,
            signal: opts?.signal,
            accessToken: opts?.accessToken,
        }),
        adminOrganization: (id, opts) => request({
            route: '/v1/admin/organizations/:id',
            path: PLATFORM_API.adminOrganization(id),
            method: 'GET',
            auth: 'bearer',
            schema: adminOrganizationDetailSchema,
            signal: opts?.signal,
            accessToken: opts?.accessToken,
        }),
    };
}
//# sourceMappingURL=client.js.map